import { Core } from "@faire/design-tokens";
import React from "react";
import styled from "styled-components";

interface IProps {
  /**
   * value of the progress bar
   */
  value: number;
  /**
   * max value of the progress bar
   * @default 100
   */
  max?: number;
  /**
   * background color of the bar
   * @default Core.surface.subdued
   */
  barColor?: string;
  /**
   * color of the progress bar
   * @default Core.border.default
   */
  valueColor?: string;
  /**
   * color of the bottom border of the progress bar
   * @default Core.border.default
   */
  valueBottomBorderColor?: string;
  /**
   * duration of the width transition animation in seconds
   * @default 1
   */
  transitionDuration?: number;
  /**
   * Test id root of the progress bar.
   * There will be 2 tests ids:
   * - `data-test-id` for the root element
   * - `data-test-id`-value for the value element
   */
  "data-test-id"?: string;
}

/**
 * Simple progress bar component, closer to native HTML <progress> element
 */
export const ProgressBar: React.FC<IProps> = ({
  value,
  max = 100,
  barColor = Core.surface.subdued,
  valueColor = Core.border.default,
  valueBottomBorderColor = Core.border.default,
  transitionDuration = 1,
  "data-test-id": dataTestId,
}) => {
  const percentage = Math.min(Math.max(0, value / max) * 100, 100);
  return (
    <Bar $backgroundColor={barColor} data-test-id={dataTestId}>
      <Value
        data-test-id={`${dataTestId}-value`}
        $width={`${percentage}%`}
        $backgroundColor={valueColor}
        $bottomBorderColor={valueBottomBorderColor}
        $transitionDuration={transitionDuration}
      />
    </Bar>
  );
};

const Bar = styled.div<{ $backgroundColor?: string }>`
  position: relative;
  width: 100%;
  height: 4px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;

const Value = styled.span<{
  $width?: string;
  $backgroundColor?: string;
  $bottomBorderColor?: string;
  $transitionDuration?: number;
}>`
  display: block;
  height: 100%;
  width: ${({ $width }) => $width};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-bottom: 0.5px solid ${({ $bottomBorderColor }) => $bottomBorderColor};
  transition: width ${({ $transitionDuration }) => $transitionDuration}s ease;
`;
